import { SubmitStatus, SubmitStatuses } from '@/util/storeUtilities';
import axios from 'axios';
import { createUseStore } from 'wired-react';
import { createStore } from 'wired-store';
import { UserAccountPayload } from '~/dtos/user-account';
import { API_URL } from '~/modules/config';
import { selectData, serializeAxiosError } from '~/modules/utilities/axios_utils';
import { handleException } from '~/modules/utilities/cross_env_utils';
import { validateEmail, validateZipCode } from '~/modules/utilities/validation_utils';
import { globalStores } from '@/util/globalStores';

type ValidationResult<T> = Partial<{
  [K in keyof T]: T[K] extends object
    ? Partial<ValidationResult<T[K]>> // Recursively apply to nested objects
    : string | null; // Replace non-objects with string | null
}>;

export type UserAccountStoreState = {
  isLoading: boolean;
  submitStatus: SubmitStatus;
  notifySuccess: string;
  notifyFailure: string;
  validationErrors: ValidationResult<UserAccountPayload>;
  userAccountPayload: UserAccountPayload;
};

const initialState: UserAccountStoreState = {
  isLoading: false,
  submitStatus: SubmitStatuses.UNINITIALIZED,
  notifySuccess: '',
  notifyFailure: '',
  validationErrors: {},
  userAccountPayload: {
    user: {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      address1: '',
      address2: '',
      city: '',
      state: '',
      zipcode: '',
      country: '',
    },
    paymentTransactions: [],
  },
};

export const USER_ACCOUNT_LOCAL_STORAGE_KEY = 'userAccountStore';

export const userAccountStore = createStore(
  initialState,
  (set, get) => ({
    changeUserInformation(changes: Partial<UserAccountStoreState['userAccountPayload']['user']>) {
      set(function changeUserInformation(draft) {
        for (const key of Object.keys(changes)) {
          draft.userAccountPayload.user[key] = changes[key];
          draft.validationErrors = validateFields(draft.userAccountPayload);
        }
      });
    },
    clearNotifySuccess() {
      set(function clearNotifySuccess(draft) {
        draft.notifySuccess = '';
      });
    },
    clearNotifyFailure() {
      set(function clearNotifyFailure(draft) {
        draft.notifyFailure = '';
      });
    },
    async getUserAccount() {
      try {
        set(function beginLoading(draft) {
          draft.isLoading = true;
        });

        const result = await axios.get(`${API_URL}/user/account`).then(selectData<UserAccountPayload>);

        set(function completeLoading(draft) {
          draft.isLoading = false;
          draft.userAccountPayload = result;
        });
      } catch (error) {
        handleException(error);
        set(function failedLoading(draft) {
          draft.isLoading = false;
          draft.notifyFailure = serializeAxiosError(error).message;
        });
      }
    },

    async updateUserAccount() {
      try {
        set(function beginSubmit(draft) {
          draft.submitStatus = SubmitStatuses.SUBMITTING;
        });
        const user = selectUserAccountInformation();
        const result = await axios.post(`${API_URL}/user/account`, user).then(selectData);

        set(function completeSubmit(draft) {
          draft.submitStatus = SubmitStatuses.SUBMITTED;
          draft.notifySuccess = `User Information Updated`;
          draft.userAccountPayload.user = result;
        });
      } catch (error) {
        handleException(error);
        set(function failedSubmit(draft) {
          draft.submitStatus = SubmitStatuses.FAILED;
          draft.notifyFailure = serializeAxiosError(error).message;
        });
      }
    },
  }),
  {
    storeName: 'userAccountStore',
    attachTo: globalStores(),
    warnAgainstAnonymousActions: IS_DEVELOPMENT,
  },
);

try {
  if (typeof localStorage !== 'undefined') {
    const localStorageState = JSON.parse(localStorage.getItem(USER_ACCOUNT_LOCAL_STORAGE_KEY) ?? 'null');
    if (localStorageState) {
      localStorageState.validationErrors = {};
      userAccountStore.setState(localStorageState);
    }
  }
} catch (error) {
  handleException(error);
}

// Save state to local storage
userAccountStore.subscribe((state) => {
  if (typeof localStorage !== 'undefined') {
    localStorage.setItem(USER_ACCOUNT_LOCAL_STORAGE_KEY, JSON.stringify(state));
  }
});

// This hook is used to connect react components to the store
export const useUserAccountStore = createUseStore(userAccountStore);

export function selectUserAccountStatus(state: UserAccountStoreState = userAccountStore.getState()) {
  return state.submitStatus;
}

export function selectIsLoading(state: UserAccountStoreState = userAccountStore.getState()) {
  return state.isLoading;
}

export function selectValidationErrors(state: UserAccountStoreState = userAccountStore.getState()) {
  return state.validationErrors;
}

export function selectUserAccountInformation(state: UserAccountStoreState = userAccountStore.getState()) {
  return state.userAccountPayload.user;
}

export function selectPaymentTransaction(state: UserAccountStoreState = userAccountStore.getState()) {
  return state.userAccountPayload.paymentTransactions;
}

export function validateFields(payload: UserAccountPayload): UserAccountStoreState['validationErrors'] {
  const validationErrors: UserAccountStoreState['validationErrors'] = {};
  const userInformationErrors: UserAccountStoreState['validationErrors']['user'] = {};

  if (!payload.user.firstName.trim()) {
    userInformationErrors.firstName = 'First name is required';
  }
  if (!payload.user.lastName.trim()) {
    userInformationErrors.lastName = 'Last name is required';
  }
  if (!payload.user.address1.trim()) {
    userInformationErrors.address1 = 'Address 1 is required';
  }
  if (!payload.user.city.trim()) {
    userInformationErrors.city = 'City is required';
  }
  if (!payload.user.state.trim()) {
    userInformationErrors.state = 'State is required';
  }

  if (!payload.user.email.trim()) {
    userInformationErrors.email = 'Email address is required';
  } else if (!validateEmail(payload.user.email)) {
    userInformationErrors.email = 'Invalid email address';
  }

  if (!payload.user.zipcode.trim()) {
    userInformationErrors.zipcode = 'Zipcode is required';
  } else if (!validateZipCode(payload.user.zipcode)) {
    userInformationErrors.zipcode = 'Invalid zipcode (5 digits required)';
  }

  //Assign user information property only if errors exist
  if (Object.keys(userInformationErrors).length > 0) {
    validationErrors.user = userInformationErrors;
  }

  return validationErrors;
}
